import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { isEmpty } from '@/utils/index';
import { ProductTracker } from '@/utils/Tracker';
import InsuraneConfig from '@/components/ExtraBindService/Insurance.config';
import ThirdPartyPlatformConfig from '@/utils/configs/ThirdPartyPlatform.config';
import CustomSkinHelper from '@/pages/product/components/CustomSkin/helper';
import { isPreSaleCommodity } from '@/utils/configs/product.config';
import styles from './index.module.less';
import { handleProductIntroduction } from '@/pages/product/config/ProductInfoHelper.config';
import classNames from 'classnames';
import MobxWrap from '@/components/MobxWrap';
import { IProductInfo } from '@/types/product';

const EXPECT_CIDS = [523, 549, 590, 636]; // 即使一个套餐也展示套餐选项

/**
 * 产品简介，如果当前套餐仅有1个，套餐简介拼接在产品简介后
 */
const Index = MobxWrap(['product', 'area', 'app'], (props) => {
  const {
    LANG,
    country: { code, X_Language },
  } = props.area;
  const { isMobile } = props.app;
  const {
    productDetail,
    productDetail: { info: pInfo = {} as IProductInfo, id: pid, VersionConfig, isNeedHidePrice, is_camera },
    curSelectCommodity: { info: cInfo = {}, id: cid },
    curCommodityList,
  } = props.product;
  const [hideMoreBtn, setHideMoreBtn] = useState(true);
  const [showMore, setShowMore] = useState(false);
  const introRef = useRef<HTMLDivElement>(null);
  const MAX_HEIGHT = isMobile ? 169 : 121; // 7行*24高度是168，PC设置最高检测高度为121，超出121即隐藏剩下内容

  // 预售商品处理产品简介，替换简介中的变量等
  if (pInfo?.introduction) {
    pInfo.introduction = handleProductIntroduction({ productDetail, country: props.area.country });
  }

  // 调整showMore按钮控制容器高度
  const handleShowMore = useCallback(
    (value) => {
      if (introRef.current) {
        const _showMore = typeof value === 'boolean' ? value : !showMore;
        introRef.current.style.height = _showMore
          ? `${introRef.current.scrollHeight}px`
          : introRef.current.scrollHeight > MAX_HEIGHT
          ? `${MAX_HEIGHT}px`
          : 'fit-content';
        setShowMore(_showMore);

        if (typeof value !== 'boolean') {
          // 点击按钮时埋点
          ProductTracker.productPage({
            action: 'intro_' + (_showMore ? 'open' : 'close'),
            productState: props.product,
          });
        }
      }
    },
    [showMore]
  );

  // 如果简介内容为空则不显示
  const isEmptyPInfo = useMemo(() => isEmpty(pInfo.introduction, true), [pInfo.introduction]);
  const isEmptyCInfo = useMemo(() => isEmpty(cInfo.introduction, true), [cInfo.introduction]);

  const showCInfo = useMemo(() => {
    // VB商品中国不显示套餐简介，只展示产品简介
    if (isNeedHidePrice) return false;
    // 定制产品页不显示套餐简介(显示在主机产品页)，只显示产品简介
    const pids = [...EXPECT_CIDS, ...CustomSkinHelper.CustomSkinPidList];
    /**
     * 2025.02.13: 主机单SKU选购流程优化
     * 【PROD】：https://arashivision.feishu.cn/wiki/XAgpwLWpQiYmZpktyxzcbB2cnZe
     *    由于选购模块会露出带有 cInfo，
     *    因此基于现在主机单SKU也会有选购模块展示，因此只要是主机产品页就不在 intro 模块展示 cInfo 。
     */
    if (is_camera) return false;
    return !pids.includes(pid) && !isEmptyCInfo && curCommodityList.length <= 1 && VersionConfig?.type !== 'group';
  }, [VersionConfig?.type, cid, curCommodityList.length, isEmptyCInfo, pid, is_camera]);

  // 额外第三方
  const ExtraIntro = useMemo(() => {
    const intro = [
      ...ThirdPartyPlatformConfig.getExtraIntroConfig({ cid, code, language: X_Language }),
      InsuraneConfig.getExtraIntro({ cid, LANG }),
    ].filter((o) => !isEmpty(o, true));

    if (!intro || intro.length === 0) return null;

    return (
      <ul className={styles.extraIntro}>
        {intro?.map((text, k) => {
          return (
            <li key={k}>
              <span dangerouslySetInnerHTML={{ __html: text }} />
            </li>
          );
        })}
      </ul>
    );
  }, [cid]);

  // 内部有值的情况下展示组件，否则隐藏
  const visible = useMemo(
    () => !isEmpty(ExtraIntro) || !isEmptyPInfo || (showCInfo && !isEmptyCInfo),
    [ExtraIntro, isEmptyCInfo, isEmptyPInfo, showCInfo]
  );

  useEffect(() => {
    // VB预售产品默认展开产品描述
    const isPreSale = isPreSaleCommodity({ cid });
    if ((isPreSale || isNeedHidePrice) && introRef.current) {
      setHideMoreBtn(true);
      introRef.current.style.height = `${introRef.current.scrollHeight}px`;
      introRef.current.style.maxHeight = 'unset';
      return;
    }

    if (visible && !!introRef.current) {
      if (introRef.current.scrollHeight > MAX_HEIGHT) {
        setHideMoreBtn(false);
        introRef.current.style.height = `${MAX_HEIGHT}px`;
        introRef.current.style.maxHeight = 'unset';
        ProductTracker.productPage({
          action: 'intro_show_showMoreBtn',
          productState: props.product,
        });
      } else {
        introRef.current.style.height = `${introRef.current.scrollHeight}px`;
        setHideMoreBtn(true);
        ProductTracker.productPage({
          action: 'intro_hide_showMoreBtn',
          productState: props.product,
        });
      }
    }
  }, [visible]);

  return (
    <div className={classNames(styles.intro, !visible && styles.hide, !hideMoreBtn && styles.hasMoreIntro)}>
      <div className={styles.contain} ref={introRef} data-show={hideMoreBtn || showMore}>
        {ExtraIntro}

        {!isEmptyPInfo && (
          <div
            className={`rich-text-style ${styles.introWrap} pInfo`}
            dangerouslySetInnerHTML={{ __html: pInfo.introduction! }}
          />
        )}

        {/* 不展示套餐简介 */}
        {showCInfo && (
          <div
            className={`rich-text-style ${styles.introWrap} cInfo`}
            dangerouslySetInnerHTML={{ __html: cInfo.introduction! }}
          />
        )}
      </div>
      <div className={styles.more} data-hide={hideMoreBtn} data-show={showMore} onClick={handleShowMore}>
        {showMore ? LANG['common.btn.showLess'] : LANG['common.btn.showMore']}
      </div>
    </div>
  );
});

export default Index;
